import { Link } from "@inertiajs/react"
import { SectionShell } from "./SectionShell"
import { SectionEditor } from "./SectionEditor"
import { FeatureListEditor } from "./FeatureListEditor"
import { PhotoPicker } from "./PhotoPicker"
import type { Entry, Lang, LibraryPhoto } from "./types"

export function EntryRenderer({
  entry,
  library,
  lang = "en",
  aiEnabled = false,
  move,
}: {
  entry: Entry
  library: LibraryPhoto[]
  lang?: Lang
  aiEnabled?: boolean
  /** Up/down handlers from the page screen — see SectionShell. */
  move?: { up?: () => void; down?: () => void }
}) {
  return (
    <SectionShell
      id={entry.id}
      title={entry.label}
      move={move}
      toggle={
        entry.kind === "fields" || entry.kind === "feature_list"
          ? { slug: entry.slug, visible: entry.visible }
          : undefined
      }
    >
      {entry.kind === "fields" && (
        <SectionEditor entry={entry} library={library} lang={lang} aiEnabled={aiEnabled} />
      )}

      {entry.kind === "feature_list" && (
        <FeatureListEditor entry={entry} lang={lang} aiEnabled={aiEnabled} />
      )}

      {entry.kind === "photos_per_site" && (
        <div className="space-y-6">
          {entry.per_site.map((site) => (
            <div key={site.site_id} className="space-y-2">
              <p className="text-sm font-medium text-ink-display">{site.site_name}</p>
              <PhotoPicker
                slotKey={entry.slot_key}
                siteId={site.site_id}
                photos={site.photos}
                library={library}
                maxItems={1}
              />
            </div>
          ))}
        </div>
      )}

      {entry.kind === "gallery_categories" && (
        <div className="space-y-6">
          {entry.categories.map((category) => (
            <div key={category.slot_key} className="space-y-2">
              <p className="text-sm font-medium text-ink-display">{category.label}</p>
              <PhotoPicker
                slotKey={category.slot_key}
                siteId={entry.site_id}
                photos={category.photos}
                library={library}
              />
            </div>
          ))}
        </div>
      )}

      {entry.kind === "note" && <p className="text-sm text-ink-muted">{entry.note}</p>}

      {entry.kind === "link_out" && (
        <div className="space-y-2">
          {entry.note && <p className="text-sm text-ink-muted">{entry.note}</p>}
          <Link href={entry.link_path} className="block text-sm text-accent-display">
            {entry.link_label}
          </Link>
        </div>
      )}
    </SectionShell>
  )
}
